import React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { TicketIssue } from "@services/tickets/types";
import { Device } from "@services/devices/types";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { Chip, Typography } from "@mui/material";
import { getSubTypeList } from "@pages/Tickets/Drawer/utils";
import { FormContext } from "@pages/Tickets/Drawer/context/formContext";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { canEditTicket } from "@common/utils/user";
import CheckIcon from "@mui/icons-material/Check";
type PaperDeviceProps = {
  device?: Device;
};
type SubTypeOptions = {
  value: string;
  title: string;
  inputValue?: string;
};

const SubTypePicker = (props: PaperDeviceProps) => {
  const { device } = props;
  const [user] = useAuthenticatedUser();
  const canEdit = canEditTicket(user);
  const { t } = useTranslation();
  const { dispatchTicket, form, dispatchHasChanged } = useContext(FormContext);
  const { ticket } = form;
  const disabled = !canEdit || ticket.status === "Closed";
  const [value, setValue] = useState<SubTypeOptions[]>([]);

  const getSubtypeName = (subtype: string) => {
    var found = getSubTypeList(t).find((item) => item.value === subtype);
    if (found) return found.title;
    return subtype;
  };
  const [customOptions, setCustomOptions] = useState<SubTypeOptions[]>(
    ticket?.issue?.subTypes?.map((subtype: string) => {
      return {
        value: subtype,
        title: getSubtypeName(subtype),
      };
    }) ?? []
  );

  useEffect(() => {
    if (ticket) {
      setValue(getTicketSubTypes());
    }
  }, [ticket]);

  const buildSubtypeItem = () => {
    let subTypes: SubTypeOptions[] = [];
    let type = device?.type ?? "";
    if (!ticket?.id) {
      if (type == "Codecs") {
        subTypes = [
          { value: "Link Down", title: t("Communication Link") },
          { value: "Ping Latency", title: t("Ping Latency") },
          { value: "Login Error", title: t("Login to Device") },
          { value: "API Error", title: t("Adapter Link") },
          {
            value: "H323 Registration",
            title: t("Gatekeeper Registration"),
          },
          {
            value: "SIP Registration",
            title: t("SIP Registration"),
          },
          {
            value: "Packet Loss Rx",
            title: t("Codec Media Packet Loss Receive (%)"),
          },
          {
            value: "Packet Loss Tx",
            title: t("Codec Media Packet Loss Send (%)"),
          },
          {
            value: "Content Packet Loss Rx",
            title: t("Codec Content Packet Loss Receive (%)"),
          },
          {
            value: "Content Packet Loss Tx",
            title: t("Codec Content Packet Loss Send (%)"),
          },
          {
            value: "Audio Jitter Rx",
            title: t("Codec Audio Jitter Receive (ms)"),
          },
          {
            value: "Audio Jitter Tx",
            title: t("Codec Audio Jitter Send (ms)"),
          },
          {
            value: "Video Jitter Rx",
            title: t("Codec Video Jitter Receive (ms)"),
          },
          {
            value: "Video Jitter Tx",
            title: t("Codec Video Jitter Send (ms)"),
          },
          {
            value: "Content Jitter Rx",
            title: t("Codec Content Jitter Receive (ms)"),
          },
          {
            value: "Content Jitter Tx",
            title: t("Codec Content Jitter Send (ms)"),
          },
          {
            value: "Frame Rate Rx",
            title: t("Codec Video Speed Receive (fps)"),
          },
          {
            value: "Frame Rate Tx",
            title: t("Codec Video Speed Send (fps)"),
          },
          {
            value: "Content Frame Rate Rx",
            title: t("Codec Content Speed Receive (fps)"),
          },
          {
            value: "Content Frame Rate Tx",
            title: t("Codec Content Speed Send (fps)"),
          },
          {
            value: "Resolution Height Rx",
            title: t("Codec Video Resolution Receive"),
          },
          {
            value: "Resolution Height Tx",
            title: t("Codec Video Resolution Send"),
          },
          {
            value: "Content Resolution Height Rx",
            title: t("Codec Content Resolution Receive"),
          },
          {
            value: "Content Resolution Height Tx",
            title: t("Codec Content Resolution Send"),
          },
        ];
      } else if (type == "Infrastructure") {
        subTypes = [
          { value: "Link Down", title: t("Communication Link") },
          { value: "Ping Latency", title: t("Ping Latency") },
          { value: "Login Error", title: t("Login to Device") },
          { value: "API Error", title: t("API Error") },
          {
            value: "portUtilization",
            title: t("MCU Ports Usage (%)"),
          },
        ];
      } else if (type == "Computer") {
        subTypes = [
          {
            value: "Component Reachability",
            title: t("Communication Link"),
          },
          { value: "Ping Latency", title: t("Ping Latency") },
          { value: "CPU Usage", title: t("PC CPU Usage (%)") },
          {
            value: "Memory Usage",
            title: t("PC Memory Usage (%)"),
          },
          {
            value: "Bandwidth Usage (In)",
            title: t("PC Bandwidth IN (Mbps)"),
          },
          {
            value: "Bandwidth Usage (Out)",
            title: t("PC Bandwidth OUT (Mbps)"),
          },
        ];
      } else if (type == "AV Devices") {
        subTypes = [
          { value: "Link Down", title: t("Communication Link") },
          { value: "API Error", title: t("API Error") },
          { value: "Ping Latency", title: t("Ping Latency") },
        ];
      } else if (type == "") {
        // not a ticket for device
        subTypes = [];
      } else {
        subTypes = [
          { value: "Link Down", title: t("Communication Link") },
          { value: "Ping Latency", title: t("Ping Latency") },
        ];
      }
    }
    return [...subTypes, ...customOptions];
  };

  const getTicketSubTypes = () => {
    return (
      ticket?.issue?.subTypes?.map((value: string) => ({
        value,
        title: getSubtypeName(value),
      })) ?? []
    );
  };

  const convertTicketSubTypes = (
    list: Omit<SubTypeOptions, "inputValue">[]
  ) => {
    return list.map((item) => convertSubtypeName(item.title));
  };

  const convertSubtypeName = (subtype: string) => {
    var found = getSubTypeList(t).find((item) => item.title === subtype);
    if (found) return found.value;
    return subtype;
  };

  const subtypeOptions: Readonly<SubTypeOptions>[] = buildSubtypeItem();

  const handleCustomOptions = (newValues: SubTypeOptions[]) => {
    const newOptions = newValues.filter(
      (val) => !subtypeOptions.some((subtype) => subtype.value === val.value)
    );
    if (newOptions.length > 0) {
      setCustomOptions((prevCustomOption) => [
        ...prevCustomOption,
        ...newOptions,
      ]);
    }
  };

  return (
    <Autocomplete
      multiple
      size="small"
      value={value ?? ""}
      id="tags-filled"
      options={subtypeOptions}
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      disabled={disabled}
      disableCloseOnSelect
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={(event, values) => {
        let tmp = [];
        for (var i = 0; i < values.length; i++) {
          if (typeof values[i] == "string") {
            var found = tmp.find((item) => item.value === values[i]);
            if (!found) {
              tmp.push({
                value: values[i] as string,
                title: values[i] as string,
              });
            }
          } else if (typeof values[i] !== undefined) {
            let test = values[i] as {
              value: string;
              title: string;
              inputValue?: string;
            };
            var found = tmp.find((item) => item.value === test.value);
            if (!found) {
              tmp.push({
                value: test.value as string,
                title: test.title as string,
              });
            }
          }
        }
        handleCustomOptions(tmp);
        setValue(tmp);
        let issue = {
          ...ticket.issue,
          ...{
            subTypes: convertTicketSubTypes(tmp),
          },
        };
        dispatchTicket({ issue: issue as TicketIssue });
        dispatchHasChanged(true);
      }}
      fullWidth
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === "string") {
          return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // Regular option
        return option.title;
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const tagProps = getTagProps({ index });
          const { key, ...rest } = tagProps; 
          return (
            <Chip
              key={key}
              size="small"
              variant="outlined"
              label={typeof option === "string" ? option : option.title}
              {...rest}
            />
          );
        })
      }
      renderOption={(props, option) => {
        const isSelected = value.some(({ value }) => value === option.value);
        return (
          <li {...props}>
            <Typography component="span" sx={{ flexGrow: 1 }}>
              {option.title}
            </Typography>
            <CheckIcon
              sx={{
                visibility: isSelected ? "visible" : "hidden",
              }}
              fontSize="small"
            />
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={t("Issue Subtype")} />
      )}
    />
  );
};

export default SubTypePicker;
